// Memoized selector for download in progress
import { memoize } from "proxy-memoize";
import { RootState } from "../../redux/store";
import { FileInfo } from "../files/files-types";
import { DeviceCapability } from "../devices/devices-types";
import { RxList, TimeRangeTs } from "../../components/detection/detection-types";
import { Animal } from "../animals/animals-types";
/** Time range as ISOString */
type TimeRange = {
  min: string;
  max: string;
};

const nowIsh = new Date().toISOString();

export const selectAnimalState = memoize((state: RootState) => {
  const animalsByTxID = new Map<string, Animal[]>(); // map of txid > animals
  state.animals.animals?.forEach(animal => {
    animal.devices.forEach(device =>
      device.transmitters.forEach(tx => {
        animalsByTxID.has(tx.displayId)
          ? animalsByTxID.set(tx.displayId, [
              ...(animalsByTxID.get(tx.displayId) as Animal[]),
              animal,
            ])
          : animalsByTxID.set(tx.displayId, [animal]);
      })
    );
  });

  return {
    animalLoading: state.animals.loading,
    animalError: state.animals.error,
    animalsByTxID,
    animals: state.animals.animals,
  };
});

export const selectStudyState = memoize((state: RootState) => {
  const selectedStudy = state.study.selectedStudy;
  const studyFileNames: string[] = selectedStudy?.files?.map(file => file.name) || [];
  const studyDeploymentIds: string[] = selectedStudy?.deployments?.map(({ id }) => id) || [];
  const studyAnimalsTxIDs: string[] = [];
  selectedStudy?.animals?.forEach(animal => {
    animal.devices.forEach(device => {
      device.transmitters.forEach(tx => {
        if (!studyAnimalsTxIDs.includes(tx.displayId)) {
          studyAnimalsTxIDs.push(tx.displayId);
        }
      });
    });
  });

  return {
    selectedStudyId: state.study.selectedId,
    selectedStudy,
    studyFileNames,
    studyDeploymentIds,
    studyAnimalsTxIDs,
  };
});

export const selectDeploymentsState = memoize((state: RootState) => {
  const studyLoaded = state.study.isLoaded;
  const selectedStudyId = state.study.selectedId;
  const deployments = state.deployments.deployments;
  const deploymentsLoading = state.deployments.loading;

  const deploymentsTimeRange: TimeRange = { min: nowIsh, max: "" };
  const rxTimeRangeMap = {} as { [serial: string]: TimeRange };
  const deploymentsRxs: string[] = [];

  if (studyLoaded && deployments && !deploymentsLoading) {
    const { studyDeploymentIds } = selectStudyState(state);
    deployments?.forEach(deployment => {
      if (!selectedStudyId || studyDeploymentIds.includes(deployment.id)) {
        deployment?.deviceAttachments?.forEach(da => {
          if (da.device?.capabilities?.includes(DeviceCapability.RECEIVER)) {
            const serial = da.device.serial;
            const min = da.start || deployment.start;
            const max = da.end || deployment.end;

            if (!rxTimeRangeMap[serial]) {
              rxTimeRangeMap[serial] = { min: nowIsh, max: "" };
            }
            rxTimeRangeMap[serial].min =
              rxTimeRangeMap[serial].min < min ? rxTimeRangeMap[serial].min : min;
            rxTimeRangeMap[serial].max =
              rxTimeRangeMap[serial].max > max ? rxTimeRangeMap[serial].max : max;
            deploymentsRxs.push(serial);
          }
        });
      }
    });

    for (const serial in rxTimeRangeMap) {
      deploymentsTimeRange.min =
        deploymentsTimeRange.min < rxTimeRangeMap[serial].min
          ? deploymentsTimeRange.min
          : rxTimeRangeMap[serial].min;
      deploymentsTimeRange.max =
        deploymentsTimeRange.max > rxTimeRangeMap[serial].max
          ? deploymentsTimeRange.max
          : rxTimeRangeMap[serial].max;
    }
  }

  return {
    deploymentsTimeRange,
    rxTimeRangeMap,
    deploymentsRxs,
  };
});

export const selectFilesState = memoize((state: RootState) => {
  const filesLoaded = state.files.isLoaded;
  const files = state.files.fileList;
  const studyLoaded = state.study.isLoaded;
  const selectedStudyId = state.study.selectedId;
  const deploymentsLoading = state.deployments.loading;

  const fileList: FileInfo[] = [];
  const fileIdToNameMap = new Map<string, string>();
  const filesTimeRange: TimeRange = {
    min: nowIsh,
    max: "",
  };
  let filesRxs: string[] = [];
  let filesHasHR = false;

  if (studyLoaded && filesLoaded && !deploymentsLoading) {
    const { studyFileNames } = selectStudyState(state);
    const { deploymentsRxs } = selectDeploymentsState(state);

    const deploymentsFiles = files
      .filter(f => deploymentsRxs.includes(f.rxLogProperties?.serial || ""))
      .map(f => f.name);

    for (const file of files) {
      if (
        !selectedStudyId ||
        studyFileNames.includes(file.name) ||
        deploymentsFiles.includes(file.name)
      ) {
        fileList.push(file);
      }
    }
    filesRxs = fileList.map(file => file.rxLogProperties?.serial || "");
    filesHasHR = fileList.some(file => file.rxLogProperties?.counts.hrExternalAccepted);

    fileList.forEach(({ rxLogProperties }) => {
      if (rxLogProperties) {
        if (rxLogProperties.maxRecordTime > filesTimeRange.max) {
          filesTimeRange.max = rxLogProperties.maxRecordTime;
        }
        if (
          rxLogProperties.minRecordTime < filesTimeRange.min &&
          rxLogProperties.minRecordTime > "1970-01-01T00:00:00Z"
        ) {
          filesTimeRange.min = rxLogProperties.minRecordTime;
        }
      }
    });

    fileList.forEach(file => {
      fileIdToNameMap.set(file.id, file.name);
    });
  }

  return {
    filesLoaded,
    fileList,
    filesRxs,
    filesTimeRange,
    filesHasHR,
    fileIdToNameMap,
  };
});

export const selectDetectionSummary = memoize((state: RootState) => {
  const summaryLoading = state.detection.summary.loading;
  const summaryError = state.detection.summary.error;
  const detectionSummary = state.detection.summary.detectionSummary;
  const filesLoaded = state.files.isLoaded;
  const studyLoaded = state.study.isLoaded;
  const deploymentsLoading = state.deployments.loading;
  const rxs: RxList = [];

  if (
    filesLoaded &&
    studyLoaded &&
    detectionSummary &&
    !summaryLoading &&
    !summaryError &&
    !deploymentsLoading
  ) {
    const { filesRxs, fileList } = selectFilesState(state);
    const { deploymentsRxs } = selectDeploymentsState(state);

    detectionSummary.receiverSummary.forEach(rs => {
      const serial = rs.receiver.serial;
      if (filesRxs.includes(serial) || deploymentsRxs.includes(serial)) {
        const model = fileList.find(fi => fi.rxLogProperties?.serial === serial)?.rxLogProperties
          ?.model;
        rxs.push({
          count: rs.count,
          rxName: model ? `${model.split("-")[0]}-${serial}` : serial,
          serial,
          idCounts: rs.idCounts,
        });
      }
    });

    const rxsFromDetections = rxs.map(rx => rx.serial);
    // Get rxs with logs but no detections / de-dup serials if there are multiple files for that serial
    const noDetectionSerials = Array.from(
      new Set(filesRxs.filter(rx => rx && !rxsFromDetections.includes(rx)))
    );
    noDetectionSerials.forEach(serial => {
      const model = fileList.find(fi => fi.rxLogProperties?.serial === serial)?.rxLogProperties
        ?.model;
      rxs.push({
        count: 0,
        rxName: model ? `${model.split("-")[0]}-${serial}` : serial,
        serial,
        idCounts: [],
      });
    });
  }
  const totalCount = rxs.map(r => r.count).reduce((acc, val) => (acc += val), 0);

  return {
    rxs,
    totalCount,
  };
});

export const selectDetectionCount = memoize((state: RootState) => ({
  count: state.detection.count.count ?? 0,
  timeCorrectedCount: state.detection.count.timeCorrectedCount ?? 0,
  loading: state.detection.count.loading,
}));

export const selectDetectionHistogram = memoize((state: RootState) => {
  const { histogram } = state.detection;
  const histLength = histogram?.countPerInterval?.length || 0;
  const histogramTimeRange: TimeRangeTs = {
    min: (histLength && histogram.countPerInterval[0].dt) || 0,
    max: (histLength && histogram.countPerInterval[histLength - 1].dt) || 0,
  };

  return {
    detectionHistogram: histogram,
    histogramTimeRange,
  };
});

export const selectDetectionAbacus = memoize((state: RootState) => {
  return {
    detectionAbacus: state.detection.abacus,
  };
});

export const selectDetectionDownloadsInProgress = memoize((state: RootState) => {
  const jobIds = Object.keys(state.detection.downloads);
  const dlInProgress = jobIds.filter(jobId => {
    const stage = state.detection.downloads[jobId].stage;
    return ["PREPARE", "DOWNLOAD"].includes(stage);
  });
  return dlInProgress;
});

export const selectAnimalConflicts = memoize((state: RootState) => {
  const { animals } = state.animals;
  const { selectedId } = state.study;
  const deviceConflictedAnimals = selectedId
    ? animals.filter(a =>
        a.studyConflicts.find(sc => sc.studyId === selectedId && sc.conflict.deviceId)
      )
    : animals.filter(a => a.conflicts.find(c => c.deviceId));

  return {
    deviceConflictedAnimals,
  };
});
